import DialCodeModal from "./DialCodeModal";
import { positiveNumberPattern } from "@utils/constants";
import cn from "classnames";
import { useTranslations } from "next-intl";
import Image from "next/image";
import React, { DetailedHTMLProps, InputHTMLAttributes } from "react";
import { useRouter } from "next/router";
import PathIcon from "@svg/PathIcon";
import usePhoneInput from "@hooks/usePhoneInput";

interface PhoneFieldProps
  extends Omit<
    DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>,
    "ref"
  > {
  label?: string;
  isRequired?: boolean;
  error?: string;
  placeholder?: string;
  className?: string;
  id?: string;
  inputClassName?: string;
  noLabelPlaceholder?: boolean;
  icon?: React.ReactNode;
  errorClassName?: string;
  positionError?: "top" | "bottom";
  isPassword?: boolean;
}

const PhoneField = React.forwardRef<HTMLInputElement, PhoneFieldProps>(
  (
    {
      error = "",
      isRequired = false,
      label = "",
      className,
      id,
      type,
      value: valueProp,
      onChange: onChangeProp,
      noLabelPlaceholder = false,
      inputClassName,
      icon = null,
      errorClassName,
      positionError = "bottom",
      isPassword,
      ...props
    },
    ref
  ) => {
    const t = useTranslations();
    const { locale } = useRouter();
    const {
      openCountries,
      setOpenCountries,
      handelSelectedDialCode,
      value,
      handelChangeValue,
      selectedDialCode,
      dialcodes
    } = usePhoneInput((e) => onChangeProp?.(e), typeof valueProp === 'string' ? valueProp : undefined);

    const _id = "input";
    return (
      <>
        <DialCodeModal
          open={openCountries}
          onClose={() => setOpenCountries(false)}
          dialcodes={dialcodes}
          onSelect={handelSelectedDialCode}
        />

        <div
          dir='ltr'
          className="md:w-full sm:w-full xs:w-full w-full p-1">
          <div className="form__group w-full h-14 bg-[#F5F5F5] rounded-xl relative">
            <input
              id={_id}
              ref={ref}
              value={value}
              onChange={(e) => {
                const v = e.target.value;
                if (
                  new RegExp(positiveNumberPattern, "i").test(v as string) ||
                  !v
                ) {
                  handelChangeValue(v);
                }
              }}
              type="text"
              className={cn(
                "form__control border-2 border-[transparent] outline-0 bg-transparent h-full w-full rounded-xl pt-[10px] pb-2 px-4 pl-24 text-sm font-normal",
                error.length ? "err-input" : ""
              )}
              autoComplete="nope"
              {...props}
            />
            <label className=" absolute top-[18px] left-0 pl-2 text-[#333] text-sm font-normal flex items-center">
              <div className="flex items-center cursor-pointer" onClick={() => setOpenCountries(true)}>
                <div
                  className=" w-6 h-6 rounded-full overflow-hidden border mr-1 relative "

                >
                  <Image
                    src={selectedDialCode?.image.url || ""}
                    alt={selectedDialCode?.name || ""}
                    fill={true}
                  />

                </div>{" "}
                <PathIcon
                  stroke="#000"
                  className={`mx-1 rotate-90`}
                />
              </div>

              <span className="px-1">{selectedDialCode?.dialcode}</span>

            </label>
            {locale?.includes("ar") ? (
              <label
                className={cn(
                  "form__label px-4 text-[#999999] text-sm font-normal absolute ",
                  error ? "err-label" : "",
                  value ? "top-label" : "",
                  "right-0"
                )}
              >
                {label}
              </label>
            ) : (
              <label
                className={cn(
                  "form__label-top px-9 text-[#999999] text-sm font-normal",
                  error ? "err-label" : "",
                  "top-label",
                  "left-0"
                )}
              >
                {label}
              </label>
            )}
          </div>
          {error && (
            <div dir={locale?.includes("ar") ? "rtl" : "ltr"} className={`w-full mt-1 pl-2 flex  justify-start `}>
              <span
                className={cn(
                  "text-red-500 text-xs font-normal",
                  errorClassName
                )}
              >
                {error}
              </span>
            </div>
          )}
        </div>
      </>
    );
  }
);

PhoneField.displayName = "PhoneField";

export default PhoneField;
